import { Component, forwardRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FieldType } from '@ngx-formly/core';
import { FilePickerComponent, FilePreviewModel } from 'ngx-awesome-uploader';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ScDialogService } from '../../../sc-dialogs/sc-dialog.service';
import { DropBoxService } from '../../form-fields/drop-box-upload/drop-box.service';
import { DemoFilePickerAdapter } from '../field-file-upload/demo-file-picker.adapter';

@Component({
  selector: 'sc-field-files-upload',
  templateUrl: './field-files-upload.component.html',
  styleUrls: ['./field-files-upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldFilesUploadComponent),
      multi: true
    }
  ]
})
export class FieldFilesUploadComponent extends FieldType implements OnInit, OnDestroy {
  @ViewChild('uploader', { static: false }) uploader: FilePickerComponent;
  adapter = new DemoFilePickerAdapter(this._googleDriveService);
  loading: boolean;
  uploading: boolean;
  defaultOptions = {
    templateOptions: {
      limit: null,
      mode: 'list',
      scrollMode: 'horizontal',
      urls: []
    }
  };

  public files$: Observable<{ file: File; url: string }[]>;
  private _filesSubject$: BehaviorSubject<File[]> = new BehaviorSubject([]);
  private _subscription$: Subject<void> = new Subject();

  constructor(private _dialogService: ScDialogService, private _googleDriveService: DropBoxService) {
    super();
  }

  public ngOnInit() {
    this.files$ = this._filesSubject$.asObservable().pipe(
      switchMap((files: File[]) => {
        if (this.to.limit && files.length > this.to.limit) {
          files = files.slice(0, this.to.limit);
        }
        if (!files.length) {
          return of([]);
        }
        const fs = files.map(file =>
          this._getUrl(file).pipe(
            map((url: string) => ({
              file,
              url
            }))
          )
        );
        return combineLatest(fs);
      })
    );
  }

  onUploadSuccess(e: FilePreviewModel) {
    this.formControl.setValue(e.fileId);
    this.options.resetModel({
      ...this.model,
      docType: e.file.type,
      fileName: e.fileName
    });
    this.uploading = false;
  }

  onFileRemove(index: number) {
    if (!this.uploader) {
      // single and preview mode
      this.formControl.setValue(null);
      this._filesSubject$.next([]);
    } else {
      const files: FilePreviewModel[] = this.uploader.files;
      files.splice(index, 1);
      this.formControl.setValue(this._getFile());
      this._filesSubject$.next(this.uploader.files.map(r => r.file as File));
    }
  }

  onFileAdded(file: FilePreviewModel) {
    this.formControl.setValue(this._getFile());
    this._filesSubject$.next(this.uploader.files.map(r => r.file as File));
  }

  openFile(fileItem: { file: File }) {
    this._dialogService.fileViewer(fileItem.file.name, fileItem.file.type, fileItem.file);
  }

  public trackByIndex = (index: number): number => index;

  public ngOnDestroy() {
    this._subscription$.next();
    this._subscription$.complete();
  }

  private _getUrl(file: File) {
    return new Observable(observer => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        observer.next(reader.result as string);
      };
    });
  }

  private _getFile() {
    if (!this.uploader.files.length) {
      return;
    }
    return this.to.mode === 'single' ? this.uploader.files[0].file : this.uploader.files.map(f => f.file);
  }
}
